import { getSourceDetail, getDefaultConfig } from '@/data/sourceConfigCatalog'

/**
 * Dashboard /admin/crawlconfig 只开放「爬虫会实际消费」的字段。
 *
 * 对应爬虫 CrawlParamsService 会合并、且适配器会读取的路径：
 * - searchConfig.keywords  → FDA Product Code / EU EMDN
 * - keywords               → BusinessWire 搜索词
 * - keyword                → 千里马
 * - searchConfig.conditions → ClinicalTrials 疾病/主题
 * - searchConfig.titleKeywords → WHO ICTRP 标题词
 * - searchTerms.topics     → PubMed / Springer
 * - query                  → EUR-Lex
 *
 * 明确不开放（爬虫不读或仅元数据）：
 * - tags / channel.keywords 分类标签
 * - PR Newswire（固定栏目 path）
 * - GlobeNewswire（按 industries，非文本关键词）
 * - BCG Search（固定 searchFilters UUID）
 */

const KEYWORD_LABELS = {
  keywords: '搜索关键词',
  keyword: '搜索关键词',
  'searchConfig.conditions': '疾病 / 主题',
  'searchTerms.topics': '主题词',
  'searchConfig.keywords': '分类编码',
  'searchConfig.titleKeywords': '标题关键词',
  query: '查询语句',
}

const MODE_LABELS = {
  api_search: 'API 检索',
  keyword_search: '关键词检索',
  literature_search: '文献检索',
  portal_keyword: '站内搜索',
  list_page: '栏目列表',
}

function whoChannelKey(sourceId) {
  const match = String(sourceId || '').match(/^who_ictrp_(.+)$/i)
  return match ? match[1] : null
}

function looksLikeFdaProductCode(value) {
  return /^[A-Z][A-Z0-9]{1,5}$/.test(String(value).trim())
}

function looksLikeEmdnCode(value) {
  return /^[JWZ][A-Z0-9]/i.test(String(value).trim())
}

/** 远端把入库标签误存进 searchConfig.keywords 时识别出来 */
function isMisfiledClassificationCodes(codes, sourceId) {
  if (!Array.isArray(codes) || !codes.length) return true
  if (/fdadevice/i.test(sourceId || '')) {
    const valid = codes.filter(looksLikeFdaProductCode)
    return valid.length < Math.min(50, codes.length * 0.8)
  }
  if (/eudamed/i.test(sourceId || '')) {
    const valid = codes.filter(looksLikeEmdnCode)
    return valid.length < Math.min(50, codes.length * 0.8)
  }
  return false
}

function classificationLabel(sourceId) {
  if (/^fdadevice_pma$/i.test(sourceId || '')) return 'Product Code（FDA PMA 分类编码）'
  if (/fdadevice/i.test(sourceId || '')) return 'Product Code（FDA 分类编码）'
  if (/eudamed/i.test(sourceId || '')) return 'EMDN / CND Code（EU 分类编码）'
  if (/vbdata/i.test(sourceId || '')) return '赛道标签'
  return null
}

function usesClassificationCodes(sourceId) {
  return /fdadevice|eudamed/i.test(sourceId || '')
}

function readPath(obj, path) {
  if (!obj || !path) return undefined
  return path.split('.').reduce((cur, part) => (cur == null ? undefined : cur[part]), obj)
}

function writePath(obj, path, value) {
  const result = JSON.parse(JSON.stringify(obj || {}))
  const parts = path.split('.')
  let cur = result
  for (let i = 0; i < parts.length - 1; i += 1) {
    const part = parts[i]
    if (!cur[part] || typeof cur[part] !== 'object' || Array.isArray(cur[part])) {
      cur[part] = {}
    }
    cur = cur[part]
  }
  const last = parts[parts.length - 1]
  if (value === undefined || value === '' || (Array.isArray(value) && !value.length)) {
    delete cur[last]
  } else {
    cur[last] = value
  }
  return result
}

function parseLines(text) {
  return String(text || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
}

function deepMerge(target, source) {
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return target ? JSON.parse(JSON.stringify(target)) : {}
  }
  if (!target || typeof target !== 'object' || Array.isArray(target)) {
    return JSON.parse(JSON.stringify(source))
  }
  const result = { ...target }
  for (const key of Object.keys(source)) {
    const sourceVal = source[key]
    const targetVal = target[key]
    if (
      sourceVal
      && typeof sourceVal === 'object'
      && !Array.isArray(sourceVal)
      && targetVal
      && typeof targetVal === 'object'
      && !Array.isArray(targetVal)
    ) {
      result[key] = deepMerge(targetVal, sourceVal)
    } else if (sourceVal !== undefined) {
      result[key] = sourceVal
    }
  }
  return result
}

/**
 * 按 sourceId / adapter 解析爬虫真正消费的可编辑字段路径。
 * 无匹配 → 不可编辑（不展示在运营配置页）。
 */
export function resolveCrawlerEditablePath(sourceId, catalog) {
  const id = String(sourceId || '')
  const adapter = String(catalog?.adapterKey || catalog?.adapter || '').replace(/-/g, '_')

  if (/^fdadevice_/i.test(id) || adapter === 'fdadevice') return 'searchConfig.keywords'
  if (/^eudamed_/i.test(id) || adapter === 'eudamed') return 'searchConfig.keywords'
  if (/^businesswire_/i.test(id) || adapter === 'businesswire') return 'keywords'
  if (/^prnewswire_/i.test(id) || adapter === 'prnewswire') return 'keywords'
  if (/^vbdata_/i.test(id) || adapter === 'vbdata') return 'keywords'
  if (/^qianlima_/i.test(id) || adapter === 'qianlima') return 'keyword'
  if (/^clinicaltrials_/i.test(id) || adapter === 'clinicaltrials') return 'searchConfig.conditions'
  if (/^who_ictrp_/i.test(id) || adapter === 'who_ictrp') return 'searchConfig.titleKeywords'
  if (/^pubmed_/i.test(id) || adapter === 'pubmed') return 'searchTerms.topics'
  if (/^springer_/i.test(id) || adapter === 'springer') return 'searchTerms.topics'
  if (/^eurlex_/i.test(id) || adapter === 'eurlex') return 'query'

  // 以下源爬虫不读「文本关键词」，不开放编辑：
  // globenewswire（industries）、bcgsearch（searchFilters）
  return null
}

function inferCrawlModeFromId(sourceId) {
  const id = sourceId || ''
  if (/^qianlima_/i.test(id)) return 'portal_keyword'
  if (/clinicaltrials|who_ictrp|fdadevice|eudamed|eurlex/i.test(id)) return 'api_search'
  if (/businesswire|prnewswire|vbdata/i.test(id)) return 'keyword_search'
  if (/pubmed|springer/i.test(id)) return 'literature_search'
  // 栏目型 / 无运营检索词
  if (/globenewswire|bcgsearch/i.test(id)) return 'list_page'
  return 'list_page'
}

/**
 * 合并 catalog 默认值与远端配置
 * 远端缺分类编码时回填默认 Product Code / EMDN 列表
 */
export function resolveChannelConfig(sourceId, remoteConfig) {
  const defaults = getDefaultChannelConfig(sourceId)
  const remote = remoteConfig && typeof remoteConfig === 'object' && !Array.isArray(remoteConfig)
    ? remoteConfig
    : {}

  let merged = deepMerge(defaults, remote)

  if (usesClassificationCodes(sourceId)) {
    // 旧后端把入库标签写在顶层 keywords：不要让它污染分类编码
    if (Array.isArray(merged.keywords) && isMisfiledClassificationCodes(merged.keywords, sourceId)) {
      delete merged.keywords
    }

    const defaultCodes = readPath(defaults, 'searchConfig.keywords')
    const mergedCodes = readPath(merged, 'searchConfig.keywords')
    if (isMisfiledClassificationCodes(mergedCodes, sourceId) && Array.isArray(defaultCodes) && defaultCodes.length) {
      merged = writePath(merged, 'searchConfig.keywords', defaultCodes)
    }
  }

  return merged
}

export function getCrawlFieldMeta(sourceId) {
  const catalog = getSourceDetail(sourceId)
  const crawlMode = catalog?.crawlMode && !['globenewswire', 'bcgsearch'].includes(catalog?.adapterKey)
    ? catalog.crawlMode
    : inferCrawlModeFromId(sourceId)

  // Globe / BCG Search：catalog 可能标成 keyword_search，但爬虫不用文本关键词
  const adapter = catalog?.adapterKey
  const forcedList = ['globenewswire', 'bcgsearch'].includes(adapter)
  const effectiveMode = forcedList ? 'list_page' : crawlMode

  const keywordsPath = resolveCrawlerEditablePath(sourceId, catalog)
  const classLabel = classificationLabel(sourceId)
  const isPrn = /prnewswire/i.test(sourceId || '') || adapter === 'prnewswire'

  return {
    sourceId,
    crawlMode: effectiveMode,
    crawlModeLabel: catalog?.crawlModeLabel || MODE_LABELS[effectiveMode] || '检索采集',
    crawlModeSummary: catalog?.crawlModeSummary || catalog?.description || '',
    keywordsPath,
    keywordsLabel: classLabel
      ? `关键词（${classLabel}）`
      : (KEYWORD_LABELS[keywordsPath] || '关键词'),
    keywordsPlaceholder: classLabel
      ? (classLabel === '赛道标签'
        ? '每行一个赛道标签名（如：诊断设备、治疗设备），须与 tagMapping 一致'
        : '每行一个分类编码（保存后爬虫按此列表采集）')
      : (isPrn
        ? '每行一个搜索词；留空则按原栏目 path 采集'
        : '每行一个'),
    // 分类标签爬虫不消费，一律不展示
    showTags: false,
    tagsPath: null,
    tagsLabel: '分类标签',
    showCrawlMode: effectiveMode !== 'list_page',
    showKeywords: Boolean(keywordsPath),
    hasEditableOps: Boolean(keywordsPath),
  }
}

export function extractKeywords(config, keywordsPath, sourceId) {
  if (!keywordsPath) return []
  if (keywordsPath === 'searchConfig.titleKeywords') {
    const channelKey = whoChannelKey(sourceId)
    const map = readPath(config, keywordsPath)
      || readPath(getDefaultChannelConfig(sourceId), keywordsPath)
    const list = channelKey ? map?.[channelKey] : null
    return Array.isArray(list) ? list.map(String) : []
  }
  let val = readPath(config, keywordsPath)
  if (
    keywordsPath === 'searchConfig.keywords'
    && usesClassificationCodes(sourceId)
    && isMisfiledClassificationCodes(val, sourceId)
  ) {
    val = readPath(getDefaultChannelConfig(sourceId), 'searchConfig.keywords')
  }
  if (
    (!Array.isArray(val) || !val.length)
    && keywordsPath === 'searchConfig.keywords'
    && usesClassificationCodes(sourceId)
  ) {
    val = readPath(getDefaultChannelConfig(sourceId), 'searchConfig.keywords')
  }
  if (Array.isArray(val) && val.length) return val.map(String)
  if (typeof val === 'string' && val.trim()) return [val.trim()]
  if (keywordsPath === 'keyword' && /^qianlima_/i.test(sourceId || '')) {
    return [sourceId.replace(/^qianlima_/i, '')]
  }
  // 动脉网：无 keywords 时用 tagIds 反查标签名，便于运营编辑
  if (keywordsPath === 'keywords' && /^vbdata_/i.test(sourceId || '')) {
    const mapping = config?.tagMapping || getDefaultChannelConfig(sourceId)?.tagMapping || {}
    const tagIds = Array.isArray(config?.tagIds) ? config.tagIds : []
    if (tagIds.length && mapping && typeof mapping === 'object') {
      const reverse = {}
      Object.entries(mapping).forEach(([name, id]) => {
        reverse[String(id)] = name
      })
      return tagIds.map((id) => reverse[String(id)] || String(id)).filter(Boolean)
    }
    return Object.keys(mapping)
  }
  if (Array.isArray(val)) return val.map(String)
  return []
}

export function keywordsToText(list) {
  return (list || []).join('\n')
}

/** @deprecated 分类标签已不再开放编辑 */
export function extractTags() {
  return []
}

/** @deprecated */
export function tagsToText(list) {
  return (list || []).join('\n')
}

/** 将表单值写回频道 config，只写爬虫会消费的字段 */
export function mergeEditableParams(originalConfig, meta, { keywordsText }) {
  let config = JSON.parse(JSON.stringify(originalConfig || {}))

  // 不再保存 tags / 误放的顶层 keywords（FDA/EU）
  if (usesClassificationCodes(meta.sourceId)) {
    delete config.tags
    if (Array.isArray(config.keywords) && isMisfiledClassificationCodes(config.keywords, meta.sourceId)) {
      delete config.keywords
    }
  } else {
    delete config.tags
  }

  if (meta.showKeywords && meta.keywordsPath) {
    const lines = parseLines(keywordsText)
    if (meta.keywordsPath === 'keyword') {
      config = writePath(config, 'keyword', lines[0] || undefined)
    } else if (meta.keywordsPath === 'searchConfig.titleKeywords') {
      const channelKey = whoChannelKey(meta.sourceId)
      if (channelKey) {
        const map = { ...(readPath(config, 'searchConfig.titleKeywords') || {}) }
        if (lines.length) {
          map[channelKey] = lines
        } else {
          delete map[channelKey]
        }
        config = writePath(
          config,
          'searchConfig.titleKeywords',
          Object.keys(map).length ? map : undefined,
        )
      }
    } else {
      config = writePath(config, meta.keywordsPath, lines.length ? lines : undefined)
    }

    // 动脉网：关键词（赛道标签名）同步写回 tagIds，爬虫两边都能读
    if (/^vbdata_/i.test(meta.sourceId || '')) {
      const mapping = config.tagMapping || getDefaultChannelConfig(meta.sourceId)?.tagMapping || {}
      const tagIds = lines
        .map((name) => mapping[name])
        .filter((id) => id !== undefined && id !== null)
      if (tagIds.length) {
        config.tagIds = tagIds
      } else if (!lines.length) {
        delete config.tagIds
      }
    }
  }

  return config
}

export function getDefaultChannelConfig(sourceId) {
  return getDefaultConfig(sourceId) || {}
}

/** 推断 t_device_datasource.datasource_id（详情 404 时也能保存） */
export function resolveParentDatasourceId(sourceId) {
  const id = String(sourceId || '').trim()
  if (!id) return ''

  const catalog = getSourceDetail(id)
  if (catalog?.adapterKey) {
    return String(catalog.adapterKey).replace(/-/g, '_')
  }

  if (/^qianlima_/i.test(id)) return 'qianlima'
  if (/^fdadevice_/i.test(id)) return 'fdadevice'
  if (/^who_ictrp_/i.test(id)) return 'who_ictrp'
  if (/^aesthetic_society_/i.test(id)) return 'aesthetic_society_meetings'
  if (/^conferenceindex_/i.test(id)) return 'conferenceindex'

  const idx = id.indexOf('_')
  return idx > 0 ? id.slice(0, idx) : id
}
